import { AxiosInstance } from 'axios'
import { InjectionKey } from '@vue/composition-api'
import { ApiResponse, success } from './responses'
import * as Z from 'zod'
import { validateVoid, validateSchema, validateUnknown } from './api.zod'
import { axiosApiResponse } from './api.axios'

export class ClientAdminApi {
  constructor(private client: AxiosInstance, private uri: string) {}

  private path(path: string): string {
    return this.uri + path
  }

  // Clients

  ListClients(): any {
    return this.client.get(this.path(`/clients`)).then(response => {
      return response?.data || []
    })
  }

  GetClient(clientId: string) {
    return this.client.get(this.path(`/clients/${clientId}`)).then(response => {
      return response?.data || []
    })
  }

  FindClients(criteria: string) {
    return this.client
      .get(this.path(`/clients/find/${encodeURI(criteria)}`))
      .then(response => {
        return response?.data || []
      })
  }

  DeleteClient(id: string) {
    return this.client.delete(this.path(`/clients/${id}`)).then(response => {
      return response?.data || []
    })
  }

  CreateClient(client: any) {
    return this.client
      .post(this.path(`/clients`), { client })
      .then(response => {
        return response?.data || []
      })
  }

  UpdateClient(client: any) {
    return this.client
      .put(this.path(`/clients/${client.id}`), { client })
      .then(response => {
        return response?.data || []
      })
  }

  // Roles

  ListRoles(): ApiResponse<Roles> {
    return Promise.resolve(
      success([
        {
          id: 'KaiAdmins',
          name: 'KAI Admins'
        },
        {
          id: 'KaiEditors',
          name: 'KAI Editors'
        },
        {
          id: 'Coaches',
          name: 'Coaches'
        },
        {
          id: 'FeatureSwitches',
          name: 'FeatureSwitches'
        }
      ])
    )
  }

  // Upload URL

  UploadUrl(conversationId: string | number): ApiResponse<UploadUrlResponse> {
    return handleUploadUrlResponse(
      this.client.get(this.path(`/uploadUrl/${conversationId}`), {
        withCredentials: true
      })
    )
  }

  // Logins

  PostLogin(): ApiResponse<void> {
    return handleVoidResponse(
      this.client.get(this.path(`/post-login`), {
        withCredentials: true
      })
    )
  }

  ListLogins(from?: string): ApiResponse<ListLoginsResult> {
    return handleListLoginsResponse(
      this.client.get(this.path(`/logins`), {
        params: { from }
      })
    )
  }

  GetLogin(login: string): ApiResponse<Login> {
    return handleLoginResponse(this.client.get(this.path(`/logins/${login}`)))
  }

  SendWelcome(login: string): ApiResponse<unknown> {
    return handleUnknownResponse(
      this.client.post(this.path(`/logins/${login}/welcomes`))
    )
  }

  ResetPassword(login: string): ApiResponse<unknown> {
    return handleUnknownResponse(
      this.client.post(this.path(`/logins/${login}/passwords`))
    )
  }

  DisableLogin(login: string): ApiResponse<unknown> {
    return handleUnknownResponse(
      this.client.post(this.path(`/logins/${login}/disable`))
    )
  }

  EnableLogin(login: string): ApiResponse<unknown> {
    return handleUnknownResponse(
      this.client.post(this.path(`/logins/${login}/enable`))
    )
  }

  AddRole(login: string, role: string): ApiResponse<Login> {
    return handleLoginResponse(
      this.client.put(this.path(`/logins/${login}/roles/${role}`))
    )
  }

  RemoveRole(login: string, role: string): ApiResponse<Login> {
    return handleLoginResponse(
      this.client.delete(this.path(`/logins/${login}/roles/${role}`))
    )
  }

  CreateLogin(login: NewLogin): ApiResponse<Login> {
    return handleLoginResponse(this.client.post(this.path(`/logins`), login))
  }

  UpdateLogin(id: string, login: UpdatedLogin): ApiResponse<Login> {
    return handleLoginResponse(
      this.client.patch(this.path(`/logins/${id}`), login)
    )
  }

  DeleteLogin(id: Login): ApiResponse<void> {
    return handleVoidResponse(this.client.delete(this.path(`/logins/${id}`)))
  }

  // Teams

  ListTeams(criteria = undefined) {
    return this.client
      .get(this.path(`/teams`), { params: criteria })
      .then(response => {
        return response?.data || []
      })
  }

  GetTeam(teamId: string) {
    return this.client.get(this.path(`/teams/${teamId}`)).then(response => {
      return response?.data || []
    })
  }

  FindTeams(criteria: string) {
    return this.client
      .get(this.path(`/teams/find/${encodeURI(criteria)}`))
      .then(response => {
        return response?.data || []
      })
  }

  DeleteTeam(id: string) {
    return this.client.delete(this.path(`/teams/${id}`)).then(response => {
      return response?.data || []
    })
  }

  CreateTeam(team: any) {
    return this.client.post(this.path(`/teams`), { team }).then(response => {
      return response?.data || []
    })
  }

  UpdateTeam(team: any) {
    return this.client
      .put(this.path(`/teams/${team.id}`), { team })
      .then(response => {
        return response?.data || []
      })
  }

  // Projects

  ListProjects() {
    return this.client.get(this.path(`/projects`)).then(response => {
      return response?.data || []
    })
  }

  GetProject(projectId: string) {
    return this.client
      .get(this.path(`/projects/${projectId}`))
      .then(response => {
        return response?.data || []
      })
  }

  FindProjects(criteria: string) {
    return this.client
      .get(this.path(`/projects/find/${encodeURI(criteria)}`))
      .then(response => {
        return response?.data || []
      })
  }

  DeleteProject(id: string) {
    return this.client.delete(this.path(`/projects/${id}`)).then(response => {
      return response?.data || []
    })
  }

  CreateProject(project: any) {
    return this.client
      .post(this.path(`/projects`), { project })
      .then(response => {
        return response?.data || []
      })
  }

  UpdateProject(project: any) {
    return this.client
      .put(this.path(`/projects/${project.id}`), { project })
      .then(response => {
        return response?.data || []
      })
  }

  // Users

  ListUsers() {
    return this.client.get(this.path(`/users`)).then(response => {
      return response?.data || []
    })
  }

  UsersForTeam(teamId: string) {
    return this.client
      .get(this.path(`/teams/${teamId}/users`))
      .then(response => {
        return response?.data || []
      })
  }

  GetUser(userId: string) {
    return this.client.get(this.path(`/users/${userId}`)).then(response => {
      return response?.data || []
    })
  }

  UserByCorrelation(correlationId: string) {
    return this.client
      .get(this.path(`/users/correlation/${correlationId}`))
      .then(response => {
        return response?.data || []
      })
  }

  UserByEmail(email: string) {
    return this.client
      .get(this.path(`/users/email/${email}`))
      .then(response => {
        return response?.data || []
      })
  }

  FindUsers(criteria: string) {
    return this.client
      .get(this.path(`/users/find/${encodeURI(criteria)}`))
      .then(response => {
        return response?.data || []
      })
  }

  DeleteUser(id: string) {
    return this.client.delete(this.path(`/users/${id}`)).then(response => {
      return response?.data || []
    })
  }

  CreateUser(user: any) {
    return this.client.post(this.path(`/users`), { user }).then(response => {
      return response?.data || []
    })
  }

  UpdateUser(user: any) {
    return this.client
      .put(this.path(`/users/${user.id}`), { user })
      .then(response => {
        return response?.data || []
      })
  }
}

export const ClientAdminApiKey: InjectionKey<ClientAdminApi> = Symbol()

export type ClientId = string
export type TeamId = string
export type RoleName = string
export type LoginId = string
export type UserId = string

export type Login = Partial<{
  id: LoginId
  name?: string
  email: string
  client: ClientId
  clientAccess: Array<ClientId>
  teamAccess: Array<TeamId>
  user: UserId
  roles: Array<RoleName>
  enabled: boolean
}>

export type Logins = Array<Login>

export interface ListLoginsResult {
  next?: string | null
  users?: Logins
}

export const loginSchema = Z.object({
  id: Z.string(),
  name: Z.string().optional(),
  email: Z.string(),
  client: Z.string(),
  clientAccess: Z.array(Z.string()),
  teamAccess: Z.array(Z.string()).optional(),
  user: Z.string().optional(),
  roles: Z.array(Z.string()),
  enabled: Z.boolean(),
  status: Z.string(),
  modifiedAt: Z.string(),
  createdAt: Z.string()
})

export const listLoginsResultSchema = Z.object({
  next: Z.string()
    .nullable()
    .optional(),
  users: Z.array(loginSchema)
})

export const newLoginSchema = Z.object({
  name: Z.string().nonempty('Name is required'),
  email: Z.string()
    .nonempty('Email is required')
    .email(),
  user: Z.string().optional(),
  clientAccess: Z.array(Z.string().nonempty()).nonempty(),
  client: Z.string().nonempty('Client is required'),
  teamAccess: Z.array(Z.string().nonempty())
})

export type NewLogin = Z.infer<typeof newLoginSchema>

export const uploadUrlSchema = Z.object({
  uploadUrls: Z.object({
    audio: Z.string(),
    video: Z.string()
  })
})

export type UploadUrlResponse = Z.infer<typeof uploadUrlSchema>

export const validateNewLogin = validateSchema(newLoginSchema)

export const updatedLoginSchema = Z.object({
  name: Z.string().nonempty('Name is required'),
  clientAccess: Z.array(Z.string().nonempty()).nonempty(),
  client: Z.string().nonempty('Client is required'),
  user: Z.string().optional(),
  teamAccess: Z.array(Z.string().nonempty())
})

export type UpdatedLogin = Z.infer<typeof updatedLoginSchema>

export const validateUpdatedLogin = validateSchema(updatedLoginSchema)

export const validateLogin = validateSchema(loginSchema)
export const handleLoginResponse = axiosApiResponse(validateLogin)
export const validateLogins = validateSchema(Z.array(loginSchema))
export const handleLoginsResponse = axiosApiResponse(validateLogins)
export const validateListLoginsResult = validateSchema(listLoginsResultSchema)
export const handleListLoginsResponse = axiosApiResponse(
  validateListLoginsResult
)
export const validateUploadUrlResponse = validateSchema(uploadUrlSchema)
export const handleUploadUrlResponse = axiosApiResponse(
  validateUploadUrlResponse
)

export const handleVoidResponse = axiosApiResponse(validateVoid)
export const handleUnknownResponse = axiosApiResponse(validateUnknown)

export interface Role {
  id: string
  name: string
}

export type Roles = Array<Role>
